import { onIdTokenChanged } from 'firebase/auth';
import { io, Socket } from 'socket.io-client';
import { auth, getFreshIdToken } from './firebase';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const SOCKET_BASE = API_BASE.endsWith('/api')
  ? API_BASE.slice(0, -4)
  : API_BASE || window.location.origin;
const NOTIFICATIONS_REALTIME_ENABLED =
  String(process.env.REACT_APP_NOTIFICATIONS_REALTIME_ENABLED ?? 'false').toLowerCase() === 'true';

let socket: Socket | null = null;
let unsubIdToken: (() => void) | null = null;
let started = false;

function emitUpdated(detail?: any) {
  try {
    window.dispatchEvent(
      new CustomEvent('notifications:updated', { detail: detail ?? null }),
    );
  } catch {
    // no-op
  }
}

async function connect(force = false) {
  let token: string | null = null;
  try {
    token = await getFreshIdToken();
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[notificationsRealtime] token fetch failed', err);
    }
    return;
  }
  if (!token) return;

  if (!force && socket?.connected) return;

  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(`${SOCKET_BASE}/notifications`, {
    path: '/socket.io',
    transports: ['websocket'],
    withCredentials: true,
    auth: { token },
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('notification.created', (payload: any) => {
    emitUpdated({ kind: 'created', payload });
  });

  socket.on('notification.unread', (payload: any) => {
    emitUpdated({ kind: 'unread', payload });
  });

  socket.on('connect_error', (err: any) => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[notificationsRealtime] connect_error', err?.message || err);
    }
  });
}

export async function startNotificationsRealtime() {
  if (!NOTIFICATIONS_REALTIME_ENABLED) return;
  if (started) return;
  started = true;

  await connect(true);

  let skipInitialTokenEvent = true;
  unsubIdToken = onIdTokenChanged(auth, async (u) => {
    if (!started) return;
    if (skipInitialTokenEvent) {
      skipInitialTokenEvent = false;
      return;
    }
    if (!u) {
      stopNotificationsRealtime();
      emitUpdated({ kind: 'signed_out' });
      return;
    }
    await connect(true);
  });
}

export function stopNotificationsRealtime() {
  started = false;
  if (unsubIdToken) {
    unsubIdToken();
    unsubIdToken = null;
  }
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}
